import { CaseType, type CaseTypeRepository } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { CaseTypeTopology, ParseCaseTypeMapper } from './parse-case-type.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';
import { MethodTypeTopology } from './parse-method-type.mapper';

export class ParseCaseTypeRepository extends ParseBaseRepository implements CaseTypeRepository {
  private static readonly COUNT_CASE_TYPES_PAGINATION_LIMIT = 25;

  private readonly caseTypeMapper = new ParseCaseTypeMapper(this.parse);

  public async getById(id: string): Promise<CaseType> {
    const caseType = await new this.parse.Query(CaseTypeTopology.TABLE).get(id, this.options);

    return this.caseTypeMapper.toDomain(caseType);
  }

  public async findWithIds(...ids: string[]): Promise<CaseType[]> {
    const parseCaseTypes = await new this.parse.Query(CaseTypeTopology.TABLE)
      .containedIn(ObjectTopology.ID, ids)
      .find(this.options);

    return parseCaseTypes.map((pct) => this.caseTypeMapper.toDomain(pct));
  }

  public async getAll(searchTerm?: string, limit?: number, lastItemName?: string): Promise<CaseType[]> {
    const query = this.buildCaseTypeQuery(searchTerm);

    query.ascending(CaseTypeTopology.NAME);

    if (limit) {
      query.limit(limit);
    }

    if (lastItemName) {
      query.greaterThan(CaseTypeTopology.NAME, lastItemName);
    }

    const parseCaseTypes = await query.find(this.options);
    return parseCaseTypes.map((pct) => this.caseTypeMapper.toDomain(pct));
  }

  public async count(searchTerm?: string): Promise<number> {
    const query = this.buildCaseTypeQuery(searchTerm);
    const generator = this.getTokenPaginationGenerator({
      limit: ParseCaseTypeRepository.COUNT_CASE_TYPES_PAGINATION_LIMIT,
      lastObjectId: undefined,
      query,
    });

    // Use the generator to count the number of case types
    let resultCount = 0;
    for await (const items of generator) {
      resultCount += items.length;
    }
    return resultCount;
  }

  public async delete(id: string): Promise<void> {
    const parseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE).createWithoutData(id);
    await parseCaseType.save(
      {
        [SoftDeletableTopology.DELETED_AT]: new Date(),
      },
      this.options
    );
  }

  private buildCaseTypeQuery(searchTerm?: string): Parse.Query {
    const query = new this.parse.Query(CaseTypeTopology.TABLE);

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(CaseTypeTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    return query;
  }

  public async countForAssetType(assetTypeId: string): Promise<number> {
    const parseAssetType = this.subclasses.getSubclass('AssetType').createWithoutData(assetTypeId);

    const methodTypeQuery = new this.parse.Query('MethodType').equalTo('assetType', parseAssetType);

    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = new this.parse.Query(CaseTypeTopology.TABLE);
      query.matchesQuery(CaseTypeTopology.METHOD_TYPES, methodTypeQuery);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseCaseTypeRepository.COUNT_CASE_TYPES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseCaseTypeRepository.COUNT_CASE_TYPES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async countByMethodType(methodTypeId: string): Promise<number> {
    const parseMethodType = this.subclasses.getSubclass('MethodType').createWithoutData(methodTypeId);

    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = new this.parse.Query(CaseTypeTopology.TABLE);
      query.containedIn(CaseTypeTopology.METHOD_TYPES, [parseMethodType]);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseCaseTypeRepository.COUNT_CASE_TYPES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseCaseTypeRepository.COUNT_CASE_TYPES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async findForMethodType(methodTypeId: string): Promise<CaseType[]> {
    const parseMethodType = this.subclasses.getSubclass(MethodTypeTopology.TABLE).createWithoutData(methodTypeId);

    const query = new this.parse.Query(CaseTypeTopology.TABLE);
    query.containedIn(CaseTypeTopology.METHOD_TYPES, [parseMethodType]);

    const parseCaseTypes = await query.find(this.options);
    return parseCaseTypes.map((pct) => this.caseTypeMapper.toDomain(pct));
  }

  public async save(caseType: CaseType): Promise<string> {
    const parseCaseType = this.caseTypeMapper.fromDomain(caseType);
    const saved = await parseCaseType.save(null, this.options);
    return saved.id;
  }

  public async findByName(name: string): Promise<CaseType[]> {
    const parseCaseTypes = await new this.parse.Query(CaseTypeTopology.TABLE)
      .equalTo(CaseTypeTopology.NAME, name)
      .find(this.options);
    return parseCaseTypes.map((pct) => this.caseTypeMapper.toDomain(pct));
  }
}
